import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { generateKeysAndUserId } from '../../engine/helpers/generateKeysAndUserId';
import { BroadcastStateService } from '../../services/broadcaststate/BroadcastStateService';
import { MediaService } from '../../services/media/MediaService';
import { MultiBroadcasterService } from '../../services/multi-broadcaster/MultiBroadcasterService';
import { generateBroadcastId } from '../../services/schedule/ScheduledBroadcast';
import { ScheduleService } from '../../services/schedule/ScheduleService';
import { useTimedFlag } from '../../utils/time';
import { parseBroadcastFromUrl } from '../../utils/url';
import { Button } from '../Button';
import { BroadcasterFooter } from '../broadcaster/BroadcasterFooter';
import { Canvas } from '../Canvas';
import { FullscreenButton } from '../FullscreenButton';
import { BroadcasterViewHud } from '../huds/BroadcasterViewHud';
import { LoadingIndicator } from '../LoadingIndicator';
import { MetricsDisplay } from '../MetricsDisplay';
import { SidebarSlot } from '../SidebarSlot';
import { Stage, StageContainer } from '../Stage';
import { SplitShell } from '../shells/SplitShell';
import { StackShell } from '../shells/StackShell';
import { VideoDisplay } from '../VideoDisplay';
import { ViewerHeader } from '../viewer/ViewerHeader';
import { BlockedPermissions } from '../views/View';
import { SpeakerLobby } from './SpeakerLobby';
export function SpeakerView() {
    const activeBroadcast = BroadcastStateService.getOutput().useActiveBroadcast();
    const [showSidebar, setShowSidebar] = useState(true);
    const [broadcastWasActivated, setBroadcastWasActivated] = useState(false);
    const multiBroadcasterService = MultiBroadcasterService.getNotificationInterface();
    const stageContainerRef = useRef(null);
    const [isHudVisible, showHud] = useTimedFlag(true, 5000);
    const [showMetrics, setShowMetrics] = useState(false);
    const mediaService = MediaService.getNotificationInterface();
    const broadcastOutputBroadcasting = MultiBroadcasterService.getOutput().useIsVideoStreaming();
    const broadcastOutputReady = MultiBroadcasterService.getOutput().useReady();
    const mediaOutputMicrophonePermissioned = MediaService.getOutput().useMicrophonePermissioned();
    const mediaOutputCameraPermissioned = MediaService.getOutput().useCameraPermissioned();
    const mediaOutputMediaStreamData = MediaService.getOutput().useMediaStreamData();
    const handleCanvasMount = useCallback((canvas) => {
        if (canvas) {
            multiBroadcasterService.notifyCanvasMounted(canvas);
        }
    }, [multiBroadcasterService]);
    const onRequestMediaPermissionsButtonClicked = () => {
        mediaService.notifyMediaPermissionsRequested({
            audio: true,
            video: true,
        });
    };
    useEffect(() => {
        mediaService.notifyStartCaptureRequested();
        return () => {
            mediaService.notifyStopCaptureRequested();
        };
    }, [mediaService]);
    useEffect(() => {
        return () => {
            MultiBroadcasterService.getNotificationInterface().notifyDeactivateBroadcast();
        };
    }, []);
    // Trigger the activation of the broadcast if
    // it is not already active and return.
    if (activeBroadcast === undefined) {
        const broadcast = parseBroadcastFromUrl(new URL(window.location.href));
        let scheduledBroadcast = broadcast
            ? ScheduleService.getInstance().getScheduledBroadcast(broadcast.streamId)
            : undefined;
        // If no scheduled broadcast exists, create and save a new one based on URL
        if (scheduledBroadcast === undefined && broadcast !== undefined) {
            const { privateKey } = generateKeysAndUserId();
            scheduledBroadcast = {
                id: generateBroadcastId(),
                ...broadcast,
                broadcasterPrivateKey: privateKey,
            };
            ScheduleService.getInstance().notifyAddScheduledBroadcast(scheduledBroadcast);
        }
        BroadcastStateService.getInstance().notifyActivateBroadcast(scheduledBroadcast);
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx(LoadingIndicator, { loading: broadcastWasActivated &&
                    (!broadcastOutputReady || !broadcastOutputBroadcasting) }), _jsx(SpeakerViewRoot, { onTouchStart: () => {
                    showHud();
                }, onMouseDown: () => {
                    showHud();
                }, onMouseOver: () => {
                    showHud();
                }, before: _jsx(ViewerHeader, {}), after: _jsx(BroadcasterFooter, { showSidebar: showSidebar, onSidebarClick: () => setShowSidebar((s) => !s), broadcastServiceIsRunning: true, isBroadcasting: broadcastOutputBroadcasting, onGoLiveClick: () => {
                        setBroadcastWasActivated(true);
                        multiBroadcasterService.notifyActivateBroadcast(activeBroadcast);
                    }, onEndBroadcastClick: () => {
                        multiBroadcasterService.notifyDeactivateBroadcast();
                        setBroadcastWasActivated(false);
                    }, broadcastContext: "multi-broadcaster", onVideoQualityChanged: (_quality) => {
                        // TODO: MULTIBROADCASTER - implement call to MultiBroadcasterService to change video quality
                    } }), children: _jsx(SplitShell, { isSidebarOpen: showSidebar, sidebar: _jsx(SidebarSlot, {}), children: _jsxs(StageContainer, { ref: stageContainerRef, children: [_jsx(Stage, { children: mediaOutputMicrophonePermissioned ||
                                    mediaOutputCameraPermissioned ||
                                    mediaOutputMediaStreamData.isSharingScreen ? (_jsxs(_Fragment, { children: [_jsx(VideoDisplay, { fit: "contain", component: broadcastWasActivated && (_jsx(StyledCanvas, { onCanvasMount: handleCanvasMount, "$isVisible": broadcastOutputBroadcasting })), hud: broadcastOutputBroadcasting && (_jsx(BroadcasterViewHud, { visible: isHudVisible, fullscreenButton: _jsx(FullscreenButton, { element: stageContainerRef.current }), onToggleMetrics: () => setShowMetrics(!showMetrics), showMetrics: showMetrics })) }), !broadcastOutputBroadcasting && (_jsx(SpeakerLobby, {}))] })) : (_jsx(BlockedPermissions, { children: _jsx(Button, { type: "button", kind: "secondary", size: "big", padding: "normal", onClick: onRequestMediaPermissionsButtonClicked, children: "Allow microphone and camera" }) })) }), showMetrics && broadcastOutputBroadcasting && (_jsx(MetricsOverlay, { children: _jsx(MetricsDisplay, {}) }))] }) }) })] }));
}
const SpeakerViewRoot = styled(StackShell) `
    --tv-ui-VideoContainer-video-border-radius: var(--tv-ui-border-radius-1);
    height: 100vh;

    @media (min-width: 60rem) {
        --SplitShell_SidebarWidth: 25.25rem;
    }
`;
const StyledCanvas = styled(Canvas) `
    visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
    pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
`;
const MetricsOverlay = styled.div `
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 1000;
    pointer-events: none;

    /* Make the component itself non-interactive but allow children to be interactive */
    > * {
        pointer-events: auto;
    }
`;
